import React, {useCallback} from "react";
import {Editor} from "@tinymce/tinymce-react";
import Box from "@mui/material/Box";
import {uploadImage} from "../../services/MediaService";

// WYSIWYG editor that stores its value as an HTML string. Used for rich recipe
// content so coaches can format the body (headings, lists, colors, alignment,
// links, images, embedded video and tables) and athletes see the exact same
// formatting in the mobile app (which renders the HTML in a WebView).
//
// TinyMCE is self-hosted (GPL) — its assets are copied into /public/tinymce by
// the `copy:tinymce` npm script (wired into start/build), and loaded from there
// via `tinymceScriptSrc`. No API key / cloud calls are involved.

const TINYMCE_SRC = `${process.env.PUBLIC_URL || ""}/tinymce/tinymce.min.js`;

const PLUGINS = [
    "advlist",
    "autolink",
    "lists",
    "link",
    "image",
    "table",
    "fullscreen",
    "help",
    "directionality",
    "visualblocks",
    "autoresize",
    "searchreplace",
    "wordcount",
];

// Grouped toolbar (RTL-aware): headings, inline styles, color, alignment,
// lists, links/images/tables, fullscreen and help. Insert-video and source-code
// tools are intentionally omitted, as is the reference app's AI "Assistant".
const TOOLBAR =
    "blocks fontsize | bold italic underline | forecolor backcolor | " +
    "alignright aligncenter alignleft alignjustify | rtl ltr | " +
    "bullist numlist | link image table | " +
    "removeformat fullscreen help";

// Selectable font sizes (px) for the toolbar's size dropdown. Applied as an inline
// `font-size` style, which the mobile renderer keeps and displays as-is.
const FONT_SIZE_FORMATS = "10px 12px 14px 16px 18px 20px 24px 30px 36px 48px";

// Shared base/content styling so the editing surface and the rendered mobile
// output look consistent. RTL by default; left-aligned blocks flow LTR so their
// list markers/numbers read correctly.
const CONTENT_STYLE = `
    body {
        font-family: -apple-system, "Helvetica Neue", Roboto, sans-serif;
        font-size: 14px;
        line-height: 1.6;
        direction: rtl;
        text-align: right;
        color: #1F2933;
    }
    h1, h2, h3 { line-height: 1.3; }
    a { color: #2563EB; }
    img { max-width: 100%; height: auto; }
    table { border-collapse: collapse; width: 100%; }
    table td, table th { border: 1px solid #CBD2D9; padding: 6px 10px; }
    iframe { max-width: 100%; }
    [style*="text-align: left"] { direction: ltr; }
`;

// Quill/TinyMCE represent an empty document as "" / "<p></p>" / "<p><br></p>".
// Treat any tag-only / whitespace-only value as empty so validation matches the
// plain-text behavior — but media-only content (an image, table, video, hr) is
// NOT empty even though it has no text.
export const isRichTextEmpty = (html) => {
    if (!html) return true;
    if (/<(img|table|iframe|video|embed|hr)\b/i.test(html)) return false;
    const text = html
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/\u200f|\u200e/g, "")
        .trim();
    return text.length === 0;
};

const RichTextEditor = ({value, onChange, placeholder, disabled = false}) => {
    // Uploads dropped/pasted/dialog-selected images to our CDN and returns the
    // resulting URL, so recipe bodies reference hosted images (never base64).
    const imagesUploadHandler = useCallback((blobInfo) => {
        const blob = blobInfo.blob();
        const file = new File([blob], blobInfo.filename() || "image", {
            type: blob.type || "image/png",
        });
        return uploadImage(file);
    }, []);

    // File-picker for the Image dialog: open a native picker, upload the chosen
    // file, then hand TinyMCE the hosted URL.
    const filePickerCallback = useCallback((cb) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = () => {
            const file = input.files && input.files[0];
            if (!file) return;
            uploadImage(file)
                .then((url) => cb(url, {title: file.name}))
                .catch(() => cb("", {}));
        };
        input.click();
    }, []);

    return (
        <Box
            sx={{
                "& .tox-tinymce": {borderColor: "#D5D7DB", borderRadius: "4px"},
            }}
        >
            <Editor
                tinymceScriptSrc={TINYMCE_SRC}
                licenseKey="gpl"
                value={value || ""}
                onEditorChange={(html) => onChange(html)}
                disabled={disabled}
                init={{
                    height: 340,
                    menubar: false,
                    branding: false,
                    promotion: false,
                    // The editor lives inside a MUI <Dialog>. Render TinyMCE's own
                    // menus/popups (e.g. the insert-table grid) within the editor
                    // container instead of a body-level portal, so they aren't
                    // clipped by the modal (the grid was collapsing to 1x1).
                    ui_mode: "split",
                    // Content is RTL; the toolbar UI stays English (the core package
                    // ships no Hebrew language pack — adding one would require bundling
                    // the community he_IL file). Icons are universal.
                    directionality: "rtl",
                    placeholder,
                    plugins: PLUGINS,
                    toolbar: TOOLBAR,
                    toolbar_mode: "wrap",
                    block_formats:
                        "פסקה=p; כותרת 1=h1; כותרת 2=h2; כותרת 3=h3",
                    font_size_formats: FONT_SIZE_FORMATS,
                    // Image handling — upload to the CDN, never inline base64.
                    automatic_uploads: true,
                    images_upload_handler: imagesUploadHandler,
                    file_picker_types: "image",
                    file_picker_callback: filePickerCallback,
                    paste_data_images: true,
                    // Tables render with visible borders (matches mobile output).
                    table_default_styles: {"border-collapse": "collapse", width: "100%"},
                    content_style: CONTENT_STYLE,
                }}
            />
        </Box>
    );
};

export default RichTextEditor;
